import React, { useState, useRef, useEffect } from "react";
import Layout from "../Layout/Layout";
import CalendarComponent from "../Components/DatePicker";
import AddNewTaskModal from "../Components/AddNewTaskModel";
import KanbanBoard from "../Components/TaskManagement/TaskMainBox";
import {
  useGoogleConnectUrl,
  useGoogleDisconnect,
} from "../Controllers/TaskManagementController";
import { toast } from "react-toastify";
import { useGetProfile } from "../Controllers/UserProfileController";
import { formatQuarterlyWeek } from "../helpers/DateHelper";

export default function TaskManagement() {
  const { data } = useGetProfile();

  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const calendarRef = useRef(null);
  const [render, setRender] = useState(false);
  const [showConnectGoogle, setShowConnectGoogle] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // Google connect (mutation)
  const googleConnectMutation = useGoogleConnectUrl();
  const googleDisconnectMutation = useGoogleDisconnect();

  const [startOfWeek, setStartOfWeek] = useState(getStartAndEndOfWeek().start);

  /* -----------------------------------------
   * Show / hide connect button based on profile
   * ----------------------------------------- */
  useEffect(() => {
    if (!data) return;

    setShowConnectGoogle(!data.google_calendar_connected);
  }, [data]);

  /* -----------------------------------------
   * Handle Google OAuth result toast
   * ----------------------------------------- */
  useEffect(() => {
    const url = new URL(window.location.href);
    const google = url.searchParams.get("google");
    const reason = url.searchParams.get("reason");

    if (google === "connected") {
      toast.success("Google Calendar connected successfully");
    }

    if (google === "failed") {
      toast.error(
        reason
          ? `Google Calendar connection failed: ${reason}`
          : "Google Calendar connection failed",
      );
    }

    // Clean URL after toast
    if (google) {
      url.searchParams.delete("google");
      url.searchParams.delete("reason");
      window.history.replaceState({}, "", url.pathname);
    }
  }, []);

  /* -----------------------------------------
   * Handlers
   * ----------------------------------------- */
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const updateTaskStatus = () => {
    if (calendarRef.current?.refreshWeekStatus) {
      calendarRef.current.refreshWeekStatus();
    }
  };

  const onUpdate = () => {
    setRender((prev) => !prev);
  };

  const handleConnectGoogle = async () => {
    try {
      const res = await googleConnectMutation.mutateAsync();

      if (!res?.url) {
        toast.error("Connect URL not received");
        return;
      }

      window.location.href = res.url;
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to connect Google Calendar",
      );
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await googleDisconnectMutation.mutateAsync();
      toast.success("Google Calendar disconnected successfully");
      setShowDisconnectModal(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to disconnect Google Calendar",
      );
    }
  };
  function getStartAndEndOfWeek(baseDate = new Date()) {
    const start = new Date(baseDate);
    const day = start.getDay();
    const daysToSubtract = day === 0 ? 6 : day - 1;
    start.setDate(start.getDate() - daysToSubtract);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  const getWeekDisplayString = () => {
    const { start, end } = getStartAndEndOfWeek(selectedDate);
    const formatDateStr = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString().slice(-2);
      return `${day}.${month}.${year}`;
    };
    return `${formatDateStr(start)} - ${formatDateStr(end)}`;
  };

  const openDisconnectModal = () => setShowDisconnectModal(true);
  const closeDisconnectModal = () => setShowDisconnectModal(false);

  /* -----------------------------------------
   * Render
   * ----------------------------------------- */
  return (
    <Layout>
      <div className="page-content">
        <div className="task-content task-main">
          <div className="task-header-container">
            <button
              className="add-task-btn add-task-btn-fix text-body-b3"
              onClick={handleShowModal}
            >
              <img src="/icons/add-square.svg" alt="Add task" />
              Add new task
            </button>

            <AddNewTaskModal
              show={showModal}
              onClose={handleCloseModal}
              onTaskAdded={updateTaskStatus}
              onUpdate={onUpdate}
            />

            <div className="calendar-wrapper-task">
              <div className="text-center mb-3">
                <span
                  className="text-heading-h3 week-date-score"
                  style={{
                    color: "#ffffff",
                    display: "flex",
                    justifyContent: "center",
                    letterSpacing: "1px",
                  }}
                >
                  {getWeekDisplayString()}
                </span>
              </div>

              <CalendarComponent
                ref={calendarRef}
                onDateSelect={handleDateSelect}
                initialDate={selectedDate}
              />

              <div className="text-center mt-3">
                <span
                  className="text-body-b1"
                  style={{
                    color: "#c8ced3",
                    letterSpacing: "0.5px",
                    fontSize: "16px",
                  }}
                >
                  {formatQuarterlyWeek(selectedDate)}
                </span>
              </div>
            </div>

            <div className="task-header-right">
              {showConnectGoogle ? (
                <button
                  type="button"
                  className="btn-1 btn-1-basic text-body-b3"
                  onClick={handleConnectGoogle}
                  disabled={googleConnectMutation.isPending}
                  style={{ height: "40px" }}
                >
                  {googleConnectMutation.isPending
                    ? "Connecting..."
                    : "Connect Google Calendar"}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-1 btn-1-basic text-body-b3"
                  onClick={openDisconnectModal}
                  disabled={googleDisconnectMutation.isPending}
                  style={{
                    height: "40px",
                    color: "#d65858",
                    borderColor: "#d65858",
                  }}
                >
                  {googleDisconnectMutation.isPending
                    ? "Disconnecting..."
                    : "Disconnect Google Calendar"}
                </button>
              )}
            </div>
          </div>

          <KanbanBoard
            selectedDate={selectedDate}
            onTaskUpdate={updateTaskStatus}
            render={render}
          />
        </div>
      </div>

      {/* Disconnect Confirmation Modal */}
      {showDisconnectModal && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="disconnectGoogleModal"
          tabIndex="-1"
          aria-labelledby="disconnectGoogleModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ margin: "42px" }}>
              <div className="modal-body">
                <p className="text-body-b2 mb-4">
                  Are you sure you want to disconnect Google Calendar?
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", background: "#db4545" }}
                    onClick={handleDisconnectGoogle}
                    disabled={googleDisconnectMutation.isPending}
                  >
                    {googleDisconnectMutation.isPending ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Disconnecting...
                      </>
                    ) : (
                      "Yes"
                    )}
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px" }}
                    onClick={closeDisconnectModal}
                    disabled={googleDisconnectMutation.isPending}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
